"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { stageHref, stageLabel, useOnboardingMode } from "@/hooks/use-onboarding-mode";
import { isSameAppPath } from "@/lib/wheelTourConversion";
import { useForcedDialogFocus } from "@/components/onboarding/use-forced-dialog-focus";

type WheelTourPathGuardProps = {
  enabled: boolean;
  pathname: string;
  expectedPath: string;
  onDrift: () => void;
  onReturn: (path: string) => void;
};

export function useWheelTourPathGuard({
  enabled,
  pathname,
  expectedPath,
  onDrift,
  onReturn,
}: WheelTourPathGuardProps) {
  const onDriftRef = useRef(onDrift);
  const onReturnRef = useRef(onReturn);
  const previousExpectedPathRef = useRef(expectedPath);
  onDriftRef.current = onDrift;
  onReturnRef.current = onReturn;

  useEffect(() => {
    const previousExpectedPath = previousExpectedPathRef.current;
    previousExpectedPathRef.current = expectedPath;
    if (!enabled || isSameAppPath(pathname, expectedPath)) return;

    // A stage transition updates context before its router navigation resolves.
    // Give that known previous→next move more time than an unrelated URL drift.
    const isExpectedStageTransition = !isSameAppPath(previousExpectedPath, expectedPath)
      && isSameAppPath(pathname, previousExpectedPath);
    const timer = window.setTimeout(() => {
      onDriftRef.current();
      onReturnRef.current(expectedPath);
    }, isExpectedStageTransition ? 5000 : 300);

    return () => window.clearTimeout(timer);
  }, [enabled, expectedPath, pathname]);

  useEffect(() => {
    if (!enabled) return;

    const guardInternalLink = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      // Wheel tabs own their stage advancement in the layout click handler.
      if (anchor.dataset.wheelTourTab) return;

      try {
        const destination = new URL(anchor.href, window.location.href);
        if (destination.origin !== window.location.origin) return;
        const destinationPath = destination.pathname === "/dashboard/wheel"
          ? "/dashboard/wheel/review"
          : destination.pathname;
        if (isSameAppPath(destinationPath, expectedPath)) return;
      } catch {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onDriftRef.current();
      onReturnRef.current(expectedPath);
    };

    document.addEventListener("click", guardInternalLink, true);
    return () => document.removeEventListener("click", guardInternalLink, true);
  }, [enabled, expectedPath]);
}

export function WheelTourExitGuard() {
  const router = useRouter();
  const dialogRef = useRef<HTMLDivElement>(null);
  const {
    isOnboardingMode,
    isGexOnboarding,
    wheelStage,
    isFreeUser,
    isSpecialUser,
    setShowTourGuard,
    setShowPricing,
    completeOnboarding,
    completeGexOnboarding,
  } = useOnboardingMode();
  const wheelRewardOffer = isOnboardingMode && !isGexOnboarding && isFreeUser && !isSpecialUser;
  useForcedDialogFocus(dialogRef);

  const handleContinue = () => {
    setShowTourGuard(false);
    if (isOnboardingMode && !isGexOnboarding) router.replace(stageHref(wheelStage));
  };

  const handleShowPricing = () => {
    setShowTourGuard(false);
    if (wheelRewardOffer) completeOnboarding();
    setShowPricing(true);
  };

  const handleSkip = () => {
    setShowTourGuard(false);
    if (isGexOnboarding) completeGexOnboarding();
    else completeOnboarding();
  };

  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wheel-tour-exit-title"
        aria-describedby="wheel-tour-exit-description"
        tabIndex={-1}
        className="w-full max-w-sm overflow-hidden rounded-lg shadow-xl animate-in fade-in zoom-in-95 duration-200"
      >
        <div className="h-1.5 bg-gradient-to-r from-transparent via-lime-300 to-transparent" />
        <div className="rounded-b-lg border border-t-0 border-border/50 bg-background">
          <div className="space-y-4 p-5 text-center sm:p-6">
            <h3 id="wheel-tour-exit-title" className="text-base font-semibold tracking-tight">
              {wheelRewardOffer ? "Finish the tour and earn $5" : "Almost there!"}
            </h3>
            <p id="wheel-tour-exit-description" className="text-sm leading-relaxed text-muted-foreground">
              {wheelRewardOffer
                ? "Complete the highlighted Wheel steps, then start a plan. We’ll add a $5 credit to your account."
                : "You’ll be able to explore the full product after completing the tour."}
            </p>
            <button
              type="button"
              className="h-10 w-full rounded-md bg-emerald-600 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
              onClick={handleContinue}
            >
              {wheelRewardOffer ? `Return to ${stageLabel(wheelStage)}` : "Continue Tour"}
            </button>
            <button
              type="button"
              className="h-9 w-full rounded-md border border-border bg-background text-sm font-medium transition-colors hover:bg-muted/50"
              onClick={handleShowPricing}
            >
              {wheelRewardOffer ? "Skip the reward - show pricing" : "I understand the product - show me pricing"}
            </button>
            {!wheelRewardOffer && (
              <button
                type="button"
                className="w-full pt-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                onClick={handleSkip}
              >
                Skip tour - I&apos;ll explore on my own
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
